import { forwardRef, useRef, useState, useEffect } from "react";

import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";
import FullCalendar from "@fullcalendar/react";
import timeGridPlugin from "@fullcalendar/timegrid";
import i18next from "i18next";
import { noop } from "neetocist";
import { dayjs } from "neetocommons/utils";
import { Typography } from "neetoui";
import PropTypes from "prop-types";
import { prop } from "ramda";
import { useTranslation } from "react-i18next";

import SubHeader from "components/SubHeader";

import "./calendar-view.scss";
import { LOCALE_CONFIG, VIEW_OPTIONS } from "./constants";
import Header from "./Header";
import { mergeRefs } from "./utils";

const direction = i18next.dir() || "ltr";

const CalendarView = forwardRef(
  (
    {
      viewOptions = VIEW_OPTIONS,
      events = [],
      handleDateClick = noop,
      handleEventClick = noop,
      eventClassNames = "",
      dayCellClassNames = "",
      validDates = { start: null, end: null },
      onDateChange = noop,
      dayMaxEvents,
      eventMaxStack,
      hideHeader = false,
      locale,
      ...otherProps
    },
    ref
  ) => {
    const calendarViewOptions = viewOptions || VIEW_OPTIONS;

    const defaultView =
      Object.values(calendarViewOptions).find(prop("default")) ||
      calendarViewOptions[Object.keys(calendarViewOptions)[0]];

    const [selectedView, setSelectedView] = useState(defaultView);

    const [selectedDate, setSelectedDate] = useState(dayjs());

    const { t } = useTranslation();

    const localeSpecificProps = LOCALE_CONFIG[locale] || {};

    const handleDateChange = date => {
      setSelectedDate(prevDate => {
        const newDate = typeof date === "function" ? date(prevDate) : date;
        onDateChange(newDate);

        return newDate;
      });
    };
    const calendarWrapperRef = useRef(null);
    const calendarRef = useRef();

    useEffect(() => {
      const calendarApi = calendarRef.current?.getApi?.();
      const wrapperElement = calendarWrapperRef.current;

      if (!calendarApi || !wrapperElement) return undefined;

      const handleResize = () => {
        calendarApi.updateSize();
      };

      const observer = new ResizeObserver(handleResize);

      observer.observe(wrapperElement);

      return () => {
        observer.disconnect();
      };
    }, []);

    return (
      <div
        className="flex-col"
        data-testid="calendar-view"
        ref={calendarWrapperRef}
      >
        {!hideHeader && (
          <SubHeader
            leftActionBlock={
              <Typography style="h2">
                {selectedDate.format("MMMM, YYYY")}
              </Typography>
            }
            rightActionBlock={
              <Header
                {...{
                  calendarRef,
                  calendarViewOptions,
                  selectedDate,
                  selectedView,
                  setSelectedDate,
                  setSelectedView,
                  validDates,
                }}
                setSelectedDate={handleDateChange}
              />
            }
          />
        )}
        <FullCalendar
          expandRows
          allDayText={t("neetoMolecules.calendarView.allDay")}
          dateClick={handleDateClick}
          dayHeaderClassNames="p-2"
          displayEventTime={false}
          eventClick={handleEventClick}
          headerToolbar={false}
          height="auto"
          initialView={selectedView.fullCalendarView}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          ref={mergeRefs(ref, calendarRef)}
          {...{
            dayCellClassNames,
            dayMaxEvents,
            direction,
            eventClassNames,
            eventMaxStack,
            events,
            locale,
            ...localeSpecificProps,
            ...otherProps,
          }}
        />
      </div>
    );
  }
);

CalendarView.displayName = "CalendarView";

CalendarView.propTypes = {
  /**
   * Views to be listed in the dropdown. The default selected view can be set by adding `default: true` to the required view.
   */
  viewOptions: PropTypes.object,
  /**
   * An array of event objects that will be displayed on the calendar.
   */
  events: PropTypes.array,
  /**
   * Function to be triggered when the user clicks on a date or a time.
   */
  handleDateClick: PropTypes.func,
  /**
   * Function to be triggered when the user clicks an event.
   */
  handleEventClick: PropTypes.func,
  /**
   * A className input for adding classNames to the outermost event element.
   */
  eventClassNames: PropTypes.string,
  /**
   * A className input for adding classNames to the <td> cell.
   */
  dayCellClassNames: PropTypes.string,
  /**
   * In dayGrid view, the max number of events within a given day, not counting the +more link.
   */
  dayMaxEvents: PropTypes.number,
  /**
   * For timeline view, the maximum number of events that stack top-to-bottom. For timeGrid view, the maximum number of events that stack left-to-right.
   */
  eventMaxStack: PropTypes.number,
  /**
   * This function will be triggered with the new selected date passed as argument
   */
  onDateChange: PropTypes.func,
  /**
   * An object containing the start and end dates in which the range given is the valid range of selectable dates.
   */
  validDates: PropTypes.shape({
    start: PropTypes.instanceOf(Date),
    end: PropTypes.instanceOf(Date),
  }),
  /**
   * This prop can be used to hide the header
   */
  hideHeader: PropTypes.bool,
  /**
   * The locale to be used for the calendar.
   */
  locale: PropTypes.string,
};

export default CalendarView;
